import React from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { motion } from 'framer-motion';
import { ScanLine, CheckCircle2, AlertTriangle, Copy, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

export default function OcrAccuracy() {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['ocr-accuracy'],
    queryFn: async () => { const { data } = await api.get('/api/ocr/accuracy'); return data; },
  });

  const stats = data || { total_receipts: 0, high_confidence: 0, medium_confidence: 0, low_confidence: 0, needs_review: 0, duplicates_found: 0, average_accuracy: 0 };

  const statCards = [
    { label: 'Total Receipts', value: stats.total_receipts, icon: ScanLine, bgColor: 'bg-blue-100', iconColor: 'text-blue-600' },
    { label: 'High Confidence', value: stats.high_confidence, icon: CheckCircle2, bgColor: 'bg-emerald-100', iconColor: 'text-emerald-600' },
    { label: 'Needs Review', value: stats.needs_review, icon: AlertTriangle, bgColor: 'bg-orange-100', iconColor: 'text-orange-500' },
    { label: 'Duplicates Found', value: stats.duplicates_found, icon: Copy, bgColor: 'bg-purple-100', iconColor: 'text-purple-600' },
  ];

  const chartData = [
    { name: 'High (85-100)', value: stats.high_confidence, fill: '#10b981' },
    { name: 'Medium (65-84)', value: stats.medium_confidence, fill: '#f59e0b' },
    { name: 'Low (<65)', value: stats.low_confidence, fill: '#ef4444' },
  ];

  const accuracy = stats.average_accuracy;
  const circumference = 2 * Math.PI * 54;
  const strokeDashoffset = circumference - (accuracy / 100) * circumference;
  const gaugeColor = accuracy >= 85 ? '#10b981' : accuracy >= 65 ? '#f59e0b' : '#ef4444';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6 lg:p-8" data-testid="ocr-accuracy-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="bg-blue-100 p-2.5 rounded-xl">
            <ScanLine className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="ocr-accuracy-title">OCR Accuracy Dashboard</h1>
            <p className="text-sm text-slate-500">AI-assessed extraction quality across all receipts</p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors shadow-sm"
          data-testid="ocr-refresh-button"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm"
            data-testid={`ocr-stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className={`w-11 h-11 rounded-xl ${stat.bgColor} flex items-center justify-center mb-3`}>
              <stat.icon className={`w-5 h-5 ${stat.iconColor}`} />
            </div>
            <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
            <p className="text-sm text-slate-500 mt-0.5">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Average Accuracy Gauge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm"
          data-testid="ocr-average-accuracy"
        >
          <h3 className="text-lg font-semibold text-slate-900 mb-6">Average Accuracy</h3>
          <div className="flex items-center justify-center">
            <div className="relative w-44 h-44">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
                {/* Background circle */}
                <circle cx="60" cy="60" r="54" fill="none" stroke="#e2e8f0" strokeWidth="10" />
                {/* Progress circle */}
                <circle
                  cx="60" cy="60" r="54" fill="none"
                  stroke={gaugeColor}
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  className="transition-all duration-1000 ease-out"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-4xl font-bold text-slate-900" data-testid="ocr-accuracy-score">{accuracy}</span>
                <span className="text-sm text-slate-400">/ 100</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Confidence Distribution Bar Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm"
          data-testid="ocr-confidence-distribution"
        >
          <h3 className="text-lg font-semibold text-slate-900 mb-6">Confidence Distribution</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', color: '#1e293b' }}
              />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={60}>
                {chartData.map((entry, index) => (
                  <Cell key={index} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>
    </div>
  );
}
